import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ReservationService } from '@app/reservation/reservation.service';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// ReactiveFormsModule from
interface CalendarData {
  time?: string;
  date: string;
  notes?: string;
  guestCount?: number;
  weather?: string;
  isAlert?: boolean;
  type?: 'staff' | 'guest' | 'all';
  fileUrl?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarData[];
}
export interface CalendarReservationData {
  date: string;
  reservationCount: number;
  guestCount: number;
  weather?: string;
  reservations?: {
    id: number;
    guestName?: string;
    peopleCount: number;
    reservedFor: string;
    startTime?: string;
    endTime?: string;
  }[];
}

interface WeekView {
  days: any[];
  bars: {
    id: number;
    info: string;
    startIndex: number;
    span: number;
  }[];
}

@Component({
  selector: 'app-calendar-widget',
  templateUrl: './calendar-widget.component.html',
  styleUrls: ['./calendar-widget.component.scss']
})
export class CalendarWidgetComponent implements OnInit {
  currentDate: Date = new Date();
  selectedDate: Date = new Date();
  minAllowedWeekDate = new Date();
  viewMode: 'month' | 'week' = 'month';
  calendarData: CalendarData[] = [];
  calendarReservationData: CalendarReservationData[] = [];
  eventForm: FormGroup;
  showFormPanel: boolean = false;
  editingDate: Date | null = null;
  editingIndex: number | null = null;
  weeks: any[] = [];
  weekDays: any[] = [];
  showModal: boolean = false;
  dragData: { event: CalendarData; fromDay: CalendarDay } | null = null;
  isLoadingAlerts: boolean;
  selectedMonthOffset: number = 0;
  weekView: WeekView = {
    days: [],
    bars: []
  };
  params: any[];
  computedWeekBars: any[] = [];
  currentWeekStart: Date;
  private alertColorMap: Record<string, string> = {};
  constructor(
    private reservationService: ReservationService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) {}
  alertMap: { [date: string]: any[] } = {};
  alerts: any[] = [];
  hours: number[] = Array.from({ length: 24 }, (_, i) => i); // 0 to 23
  getAllReservations() {
    this.reservationService
      .getAllReservationsByDate() // reservation api called
      .toPromise()
      .then(async (data: any) => {
        if (data) {
          this.calendarReservationData = data;
        }
      });
  }

  getAllAlerts(start: Date, end: Date, callback: () => void): void {
    this.isLoadingAlerts = true;

    const startDateStr = this.formatDate(start);
    const endDateStr = this.formatDate(end);

    this.reservationService.fetchAlerts(startDateStr, endDateStr).subscribe(data => {
      this.alerts = data;
      this.buildAlertMap();
      this.isLoadingAlerts = false;
      callback();
    });
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date
      .getDate()
      .toString()
      .padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  ngOnInit() {
    this.generateMonth();
    this.getAllReservations();
    this.updateVisibleOffsets();
    this.currentWeekStart = this.startOfWeekMonday(this.currentDate);
    this.computeStretchableBarsForWeek(this.currentWeekStart);
    this.minAllowedWeekDate = this.startOfWeekMonday(this.minAllowedWeekDate);
  }

  setViewMode(mode: 'month' | 'week') {
    this.viewMode = mode;

    if (mode === 'week') {
      this.generateWeek();
    }

    if (mode === 'month') {
      this.generateMonth();
    }
  }

  prevWeek() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      this.currentDate.getDate() - 7
    );
    this.generateWeek();
    this.updateSelectedMonthOffset();
    this.cdRef.detectChanges();
  }

  nextWeek() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      this.currentDate.getDate() + 7
    );
    this.generateWeek();
    this.updateSelectedMonthOffset();
    this.cdRef.detectChanges();
  }

  getEndOfWeek(date: Date): string {
    const start = this.startOfWeekMonday(date);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  generateMonth() {
    const start = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
    const end = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);

    this.getAllAlerts(start, end, () => {
      const startDay = this.getDayIndex(start);
      const daysInMonth = end.getDate();
      const calendar: any[] = [];
      let week: any[] = [];

      // Add leading empty days
      for (let i = 0; i < startDay; i++) {
        week.push({ date: null, isCurrentMonth: false, alerts: [], bars: [] });
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
        const today = new Date();
        const dateKey = this.formatDate(date);
        const isToday =
          today.getDate() === date.getDate() &&
          today.getMonth() === date.getMonth() &&
          today.getFullYear() === date.getFullYear();

        week.push({
          date,
          isToday,
          isCurrentMonth: true,
          calendarData: [],
          alerts: this.alertMap[dateKey] || []
        });

        // When full week is ready
        if (week.length === 7) {
          const bars = this.computeEventBarsForWeek(week); // compute bars for the week
          calendar.push({ days: week, bars });
          week = [];
        }
      }

      // Handle trailing empty days
      if (week.length) {
        while (week.length < 7) {
          week.push({ date: null, isCurrentMonth: false, alerts: [], bars: [] });
        }
        const bars = this.computeEventBarsForWeek(week);
        calendar.push({ days: week, bars });
      }
      this.weeks = calendar;
    });
  }

  computeEventBarsForWeek(week: any[]): any[] {
    const firstValidDay = week.find(day => !!day.date);
    const lastValidDay = [...week].reverse().find(day => !!day.date);
    const weekStart = firstValidDay ? new Date(firstValidDay.date) : null;
    const weekEnd = lastValidDay ? new Date(lastValidDay.date) : null;

    if (!weekStart || !weekEnd) return [];

    return this.alerts
      .filter(alert => {
        const alertStart = new Date(alert.dateFrom);
        const alertEnd = new Date(alert.dateTo || alert.dateFrom);
        return alertStart <= weekEnd && alertEnd >= weekStart;
      })
      .map((alert, index) => {
        const alertStart = new Date(alert.dateFrom);
        const alertEnd = new Date(alert.dateTo || alert.dateFrom);

        const barStart = alertStart < weekStart ? weekStart : alertStart;
        const barEnd = alertEnd > weekEnd ? weekEnd : alertEnd;

        const leftIndex = week.findIndex(
          day => day.date && new Date(day.date).toDateString() === barStart.toDateString()
        );

        const rightIndex = week.findIndex(
          day => day.date && new Date(day.date).toDateString() === barEnd.toDateString()
        );

        // Safe fallback
        const fromIndex = leftIndex !== -1 ? leftIndex : 0;
        const toIndex = rightIndex !== -1 ? rightIndex : 6;

        return {
          alert,
          style: {
            position: 'absolute',
            left: `${fromIndex * 14.28}%`,
            width: `${(toIndex - fromIndex + 1) * 14.28}%`,
            bottom: `${5 + index * 22}px`,
            backgroundColor: this.getColorForAlert(alert)
          }
        };
      });
  }

  getCurrentWeekDates(): any[] {
    const startOfWeek = this.startOfWeekMonday(this.currentDate);

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek.getTime());
      date.setDate(date.getDate() + i);

      const dateKey = this.formatDate(date);

      return {
        date,
        isToday: this.isToday(date),
        alerts: this.alertMap[dateKey] || []
      };
    });
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  getStats(date: Date): { reservationCount: number; guestCount: number } {
    const formatted = this.formatDate(date);
    const entry = this.calendarReservationData.find(d => d.date === formatted);

    if (!entry) {
      return { reservationCount: 0, guestCount: 0 };
    }

    return {
      reservationCount: Number(entry.reservationCount || 0),
      guestCount: Number(entry.guestCount || 0)
    };
  }

  private buildAlertMap(): void {
    const map: { [key: string]: any[] } = {};

    this.alerts.forEach(alert => {
      const start = new Date(alert.dateFrom);
      const end = new Date(alert.dateTo || alert.dateFrom);

      let current = new Date(start);
      while (current <= end) {
        const key = this.formatDate(current);
        if (!map[key]) map[key] = [];
        map[key].push(alert);
        current.setDate(current.getDate() + 1);
      }
    });

    this.alertMap = map;
  }

  isDateOnly(date: string | Date): boolean {
    const d = new Date(date);
    return d.getHours() === 0 && d.getMinutes() === 0;
  }

  getTopFromTime(dateStr: string): string {
    const d = new Date(dateStr);
    const minutes = d.getHours() * 60 + d.getMinutes();
    return `${minutes}px`;
  }

  getHeightFromTimeRange(start: string, end?: string): string {
    const s = new Date(start).getTime();
    const e = end ? new Date(end).getTime() : s + 60 * 60 * 1000; // default 1hr
    const diffMin = Math.max((e - s) / (1000 * 60), 30);
    const clamped = Math.min(diffMin, 720); // clamp 12hrs max
    return `${clamped}px`;
  }

  getStartOfWeek(date: Date): Date {
    return this.startOfWeekMonday(date);
  }

  canGoPrev(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (this.viewMode === 'month') {
      const currentMonthStart = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return currentMonthStart.getTime() > thisMonthStart.getTime();
    } else {
      const firstDayOfWeek = new Date(this.weekDays[0].date);
      firstDayOfWeek.setHours(0, 0, 0, 0);
      const startOfThisWeek = this.getStartOfWeek(today);
      return firstDayOfWeek.getTime() > startOfThisWeek.getTime();
    }
  }

  generateWeek() {
    const current = new Date(this.currentDate);
    const startOfWeek = this.startOfWeekMonday(current);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    this.getAllAlerts(startOfWeek, endOfWeek, () => {
      const week: any[] = [];

      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const dateKey = this.formatDate(date);

        week.push({
          date,
          isToday: this.isToday(date),
          isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
          calendarData: [],
          alerts: this.alertMap[dateKey] || []
        });
      }

      this.weekView = {
        days: week,
        bars: []
      };

      this.computeStretchableBarsForWeek(startOfWeek);
    });
  }

  baseDate: Date = new Date();
  currentOffset: number = 0;
  minOffset: number = -11;
  maxOffset: number = 11;
  monthButtonRange: number = 6;
  visibleOffsets: number[] = [];

  addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  }

  getMonthLabel(offset: number): string {
    const date = this.addMonths(this.baseDate, offset);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  isMonthOffsetSelected(offset: number): boolean {
    return offset === this.selectedMonthOffset;
  }

  setMonthOffset(offset: number): void {
    if (this.currentOffset === offset) return;
    this.currentOffset = offset;
    const today = new Date();
    this.currentDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    if (this.viewMode === 'month') {
      this.generateMonth();
    } else {
      this.generateWeek();
    }
    this.updateVisibleOffsets();
  }

  prevMonth(): void {
    if (this.currentOffset > this.minOffset) {
      this.setMonthOffset(this.currentOffset - 1);
    } else {
      console.log('Min offset reached');
    }
    this.updateSelectedMonthOffset();
  }

  nextMonth(): void {
    if (this.currentOffset < this.maxOffset) {
      this.setMonthOffset(this.currentOffset + 1);
    } else {
      console.log('Max offset reached');
    }
    this.updateSelectedMonthOffset();
  }

  getVisibleOffsets(): number[] {
    const half = Math.floor(this.monthButtonRange / 2);
    let start = this.currentOffset - half;

    if (start < this.minOffset) {
      start = this.minOffset;
    }

    let end = start + this.monthButtonRange - 1;

    if (end > this.maxOffset) {
      end = this.maxOffset;
      start = Math.max(this.minOffset, end - this.monthButtonRange + 1);
    }

    const offsets: number[] = [];
    for (let i = start; i <= end; i++) {
      offsets.push(i);
    }

    return offsets;
  }

  updateVisibleOffsets() {
    const half = Math.floor(this.monthButtonRange / 2);
    let start = this.currentOffset - half;

    if (start < this.minOffset) {
      start = this.minOffset;
    }

    let end = start + this.monthButtonRange - 1;

    if (end > this.maxOffset) {
      end = this.maxOffset;
      start = Math.max(this.minOffset, end - this.monthButtonRange + 1);
    }

    const offsets: number[] = [];
    for (let i = start; i <= end; i++) {
      offsets.push(i);
    }

    this.visibleOffsets = offsets;
  }

  hasStats(date: any): boolean {
    const dateObj = date instanceof Date ? date : new Date(date);
    const stats = this.getStats(dateObj);
    return !!(stats && (stats.reservationCount || stats.guestCount));
  }

  getWeekRowHeight(week: any): number {
    const barsCount = week.bars ? week.bars.length : 0;
    const baseHeight = 100;
    const barHeight = 15;
    const spacingPerBar = 4;

    return baseHeight + barsCount * (barHeight + spacingPerBar);
  }

  getWeekStretchableBarStyle(bar: any): { [key: string]: string } {
    const dayWidth = 100 / 7;
    return {
      position: 'absolute',
      top: '0px',
      left: `${bar.startIndex * dayWidth}%`,
      width: `${bar.span * dayWidth}%`,
      height: '20px',
      background: '#009688',
      color: 'white',
      'text-align': 'center',
      'line-height': '20px',
      'font-size': '12px',
      'z-index': '10'
    };
  }

  computeStretchableBarsForWeek(startOfWeek: Date) {
    const bars: any[] = [];
    const weekStart = new Date(startOfWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    this.alerts.forEach((alert: any) => {
      const alertStart = new Date(alert.dateFrom);
      const alertEnd = new Date(alert.dateTo);
      if (alertEnd < weekStart || alertStart > weekEnd) return;
      const clampedStart = alertStart < weekStart ? weekStart : alertStart;
      const clampedEnd = alertEnd > weekEnd ? weekEnd : alertEnd;
      const startIndex = Math.floor((clampedStart.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24));
      const span = Math.floor((clampedEnd.getTime() - clampedStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      bars.push({
        id: alert.id,
        info: alert.info,
        startIndex,
        span,
        color: this.getColorForAlert(alert)
      });
    });

    // Update weekView.bars
    this.weekView = {
      ...this.weekView,
      bars
    };
  }

  getBarGrid(bar: { startIndex: number; span: number }): string {
    return `${bar.startIndex + 1} / span ${bar.span}`;
  }

  getEventBarStyle(bar: any): any {
    const dayWidthPercentage = 100 / 7;
    const left = bar.startIndex * dayWidthPercentage;
    const width = bar.span * dayWidthPercentage;
    return {
      position: 'absolute',
      top: '0',
      left: `${left}%`,
      width: `${width}%`,
      height: '100%',
      backgroundColor: bar.color || 'rgba(100, 149, 237, 0.5)',
      borderRadius: '4px',
      zIndex: 1,
      overflow: 'hidden',
      fontSize: '12px',
      paddingLeft: '4px'
    };
  }

  updateSelectedMonthOffset() {
    const today = new Date();
    const monthDiff =
      (this.currentDate.getFullYear() - today.getFullYear()) * 12 + (this.currentDate.getMonth() - today.getMonth());
    this.selectedMonthOffset = monthDiff;
  }

  getDayIndex(date: Date): number {
    const day = date.getDay();
    return (day + 6) % 7;
  }

  getDayDiff(start: Date, end: Date): number {
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor((end.getTime() - start.getTime()) / oneDay);
  }

  selectedAlerts: any[] | null = null;
  selectedAlertDate: Date | null = null;

  onDayClick(day: any, event: MouseEvent): void {
    this.selectedDate = day.date;
    this.toggleAlertDetails(day.alerts, event, day.date);
  }

  toggleAlertDetails(alerts: any[], event: MouseEvent, date: Date): void {
    event.stopPropagation();
    if (!alerts || alerts.length === 0) {
      this.selectedAlerts = null;
      this.selectedAlertDate = null;
      return;
    }

    const isSame = this.selectedAlerts === alerts;
    this.selectedAlerts = isSame ? null : alerts;
    this.selectedAlertDate = isSame ? null : date;
  }

  navigateToBook(currentDate: Date, event: MouseEvent) {
    event.stopPropagation();
    const selectedDate = new Date(currentDate);
    const clientId = this.route.snapshot.paramMap.get('clientId');
    this.router.navigate(['/client/' + clientId + '/reservation/book'], {
      queryParams: { date: selectedDate }
    });
  }

  private getColorForAlert(alert: any): string {
    let key = '';
    if (alert) {
      if (alert.id) {
        key = String(alert.id);
      } else if (alert.info) {
        key = String(alert.info);
      }
    }
    if (!this.alertColorMap[key]) {
      const nextIdx = Object.keys(this.alertColorMap).length;
      this.alertColorMap[key] = getRandomColor(nextIdx); // reuse your CSS-var palette
    }
    return this.alertColorMap[key];
  }

  private startOfWeekMonday(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - this.getDayIndex(d)); // 0=Mon… so subtract that many days
    return d;
  }
}

function getRandomColor(index: number): string {
  const cssVars = [
    '--primary-color', // teal
    '--alert', // red
    '--lightprimary-color', // yellowish green
    '--darkprimary-color', // dark teal
    '--sucess-color', // green
    '--bg-inactive-toggle', // lavender
    '--bg-shadow-color', // orange-ish
    '--darksecondary-color' // navy blue-ish
  ];

  const cssVar = cssVars[index % cssVars.length];
  const color = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim();
  return color || '#009688'; // fallback if var not found
}
